import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Eye, EyeOff, Check, X, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MjY5MjMyOXww&ixlib=rb-4.1.0&q=85";

// Mirrors the backend's blocklist (server.py _COMMON_PW) so obviously weak
// passwords get caught before a round-trip, not just after submit.
const COMMON_PASSWORDS = new Set([
  "password", "12345678", "azerty123", "qwerty123", "111111111",
  "motdepasse", "admin123", "letmein123", "welcome123", "abcdefgh",
]);

function isValidFrOrBePhone(phone) {
  try {
    return isValidPhoneNumber(phone, "FR") || isValidPhoneNumber(phone, "BE");
  } catch {
    return false;
  }
}

function getPasswordChecks(password) {
  return {
    length: password.length >= 8,
    letterAndDigit: /[A-Za-z]/.test(password) && /\d/.test(password),
    notCommon: password.length === 0 || !COMMON_PASSWORDS.has(password.toLowerCase()),
  };
}

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: "", color: "" };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score, label: "Trop faible", color: "bg-red-500" };
  if (score === 2) return { score, label: "Faible", color: "bg-orange-500" };
  if (score === 3) return { score, label: "Moyen", color: "bg-amber-500" };
  if (score === 4) return { score, label: "Bon", color: "bg-lime-600" };
  return { score, label: "Excellent", color: "bg-green-600" };
}

const CheckRow = ({ ok, children }) => (
  <li className={`flex items-center gap-1.5 transition-colors ${ok ? "text-green-700" : "text-ash"}`}>
    {ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <X className="h-3.5 w-3.5 shrink-0" />}
    {children}
  </li>
);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checks = getPasswordChecks(form.password);
  const strength = getPasswordStrength(form.password);
  const passwordValid = checks.length && checks.letterAndDigit && checks.notCommon;
  const confirmMismatch = form.confirmPassword.length > 0 && form.confirmPassword !== form.password;

  const submit = async (e) => {
    e.preventDefault();
    setPasswordTouched(true);
    setConfirmTouched(true);
    if (!passwordValid) {
      setError("Le mot de passe ne respecte pas les critères ci-dessous.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    if (!isValidFrOrBePhone(form.phone)) {
      setError("Format de téléphone invalide (ex: 06 12 34 56 78 ou +32 470 12 34 56)");
      return;
    }
    setError("");
    setLoading(true);
    const res = await register(form.name, form.email, form.password, form.phone);
    setLoading(false);
    if (res.ok) navigate("/compte");
    else setError(res.error);
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)]">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="back-to-home" className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink transition-colors mb-6">
            <ChevronLeft className="h-4 w-4" /> Accueil
          </Link>
          <Link to="/" aria-label="Déstockage Luminaires — accueil" className="inline-flex"><Logo to={null} size="md" variant="dark" testId="register-logo" /></Link>
          <h1 className="font-serif text-4xl text-ink tracking-tight mt-8">Créer un compte</h1>
          <p className="text-graphite mt-2 text-sm">Suivez vos commandes et gagnez du temps.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Nom complet</label>
              <Input data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-2 h-12 rounded-sm border-stone bg-white" placeholder="Jean Dupont" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Email</label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-2 h-12 rounded-sm border-stone bg-white" placeholder="vous@email.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Téléphone</label>
              <Input data-testid="register-phone" type="tel" value={form.phone} onChange={set("phone")} required className="mt-2 h-12 rounded-sm border-stone bg-white" placeholder="06 12 34 56 78" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Mot de passe</label>
              <div className="relative mt-2">
                <Input
                  data-testid="register-password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  onBlur={() => setPasswordTouched(true)}
                  required
                  minLength={8}
                  className="h-12 rounded-sm border-stone bg-white pr-11"
                  placeholder="8 caractères min, dont 1 lettre + 1 chiffre"
                />
                <button
                  type="button"
                  data-testid="register-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-0 top-0 h-12 w-11 flex items-center justify-center text-ash hover:text-ink"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {form.password && (
                <div className="mt-2.5">
                  <div className="h-1 w-full bg-stone rounded-full overflow-hidden">
                    <div
                      className={`h-full ${strength.color} transition-all duration-300`}
                      style={{ width: `${(strength.score / 5) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-ash mt-1">{strength.label}</p>
                </div>
              )}

              <ul className="mt-2.5 space-y-1 text-xs">
                <CheckRow ok={checks.length}>Au moins 8 caractères</CheckRow>
                <CheckRow ok={checks.letterAndDigit}>Au moins 1 lettre et 1 chiffre</CheckRow>
              </ul>
              {passwordTouched && !passwordValid && (
                <p className="text-xs text-destructive mt-1.5">
                  {!checks.notCommon ? "Ce mot de passe est trop courant, choisissez-en un autre." : "Le mot de passe doit respecter les critères ci-dessus."}
                </p>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Confirmer le mot de passe</label>
              <div className="relative mt-2">
                <Input
                  data-testid="register-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={set("confirmPassword")}
                  onBlur={() => setConfirmTouched(true)}
                  required
                  className="h-12 rounded-sm border-stone bg-white pr-11"
                  placeholder="Ressaisissez votre mot de passe"
                />
                <button
                  type="button"
                  data-testid="register-confirm-toggle"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-0 top-0 h-12 w-11 flex items-center justify-center text-ash hover:text-ink"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmTouched && confirmMismatch && (
                <p data-testid="register-confirm-error" className="text-xs text-destructive mt-1.5">Les mots de passe ne correspondent pas.</p>
              )}
              {confirmTouched && !confirmMismatch && form.confirmPassword && (
                <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Les mots de passe correspondent.</p>
              )}
            </div>

            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
              {loading ? "Création..." : "Créer mon compte"}
            </button>
          </form>

          <p className="text-sm text-graphite mt-6">
            Déjà inscrit ?{" "}
            <Link to="/connexion" data-testid="goto-login" className="text-ink underline underline-offset-2">Se connecter</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO} alt="Intérieur" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/40" />
      </div>
    </div>
  );
}
